import { useCallback, useMemo } from "react";

import { MultiAutoCompleteItem } from "@/components/MultiAutoComplete/MultiAutoComplete";
import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";
import { useGetIndustriesQuery } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { CompanyEnrichAutoCompleteItem } from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import { useCompanyEnrichLookups } from "@/pages/Private/pages/CompanyEnrich/components/useCompanyEnrichLookups";
import { DEPARTMENT_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/department.constants";
import { EMPLOYEE_RANGES } from "@/pages/Private/pages/CompanyEnrich/constants/employees.constants";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";
import { Industry } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

/** Only the top-level departments — the sub-departments are too fine for a first list. */
const TOP_LEVEL_DEPARTMENTS = DEPARTMENT_OPTIONS.filter((option) => !option.id.includes("/"));

export interface OnboardingFilterOptions {
	seniorityOptions: MultiAutoCompleteItem[];
	departmentOptions: MultiAutoCompleteItem[];
	employeeOptions: MultiAutoCompleteItem[];
	industryOptions: MultiAutoCompleteItem[];
	/** Industry name (lower-cased) → the filter object the API expects. */
	industriesByName: Map<string, Industry>;
	/** False only while the industry lookup is still in flight. */
	areIndustriesReady: boolean;
	fetchCountries: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchCompanyCountries: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	countryTitleOf: (code: string) => string;
	fetchMapCountries: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchMapCities: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	/** Person positions of the lead database (`PersonFiltersSection`). */
	fetchPositions: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	/** Company keywords of the lead database (`AdvancedSearchFiltersSection`). */
	fetchKeywords: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	/** Company domains for the lookalike seeds (`LookalikeSeedCard`). */
	fetchCompanyDomains: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	/** Job titles found on scraped job offers (`CreateJobForm`). */
	fetchJobTitles: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
}

/**
 * The lookups the onboarding config forms need, on top of the shared
 * CompanyEnrich ones: the fixed option lists as autocomplete items, an
 * industry-name index so a recommended industry can be turned into a filter,
 * and the Google Maps country/city suggestions.
 */
export const useOnboardingFilterOptions = (): OnboardingFilterOptions => {
	const {
		fetchCountries,
		fetchCompanyCountries,
		fetchPositions,
		fetchKeywords,
		fetchCompanyDomains,
		countryNameMap,
		industryOptions,
		industriesData,
	} = useCompanyEnrichLookups();
	const { isSuccess: industriesLoaded, isError: industriesFailed } = useGetIndustriesQuery();
	const [getFilterByProperty] = useLazyGetFilterByPropertyQuery();

	const industriesByName = useMemo(
		() => new Map(industriesData.map((industry) => [industry.name.toLowerCase(), industry])),
		[industriesData]
	);

	const fetchListProperty = useCallback(
		async (propertyName: string, query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await getFilterByProperty({ propertyName, searchTerm: query }).unwrap();

			// These properties are stored as plain names, so the value doubles as the item id.
			return result.data.map((entry) => ({ id: entry.value, title: entry.value }));
		},
		[getFilterByProperty]
	);

	const fetchMapCountries = useCallback(
		(query: string) => fetchListProperty("country", query),
		[fetchListProperty]
	);

	const fetchMapCities = useCallback(
		(query: string) => fetchListProperty("city", query),
		[fetchListProperty]
	);

	const fetchJobTitles = useCallback(
		(query: string) => fetchListProperty("title", query),
		[fetchListProperty]
	);

	const countryTitleOf = useCallback(
		(code: string) => countryNameMap.current.get(code) ?? code,
		[countryNameMap]
	);

	return {
		seniorityOptions: SENIORITY_OPTIONS,
		departmentOptions: TOP_LEVEL_DEPARTMENTS,
		employeeOptions: EMPLOYEE_RANGES,
		industryOptions,
		industriesByName,
		areIndustriesReady: industriesLoaded || industriesFailed,
		fetchCountries,
		fetchCompanyCountries,
		countryTitleOf,
		fetchMapCountries,
		fetchMapCities,
		fetchPositions,
		fetchKeywords,
		fetchCompanyDomains,
		fetchJobTitles,
	};
};

/** Resolves recommended industry names to the `{ name, naicsCodes }` filter objects. */
export const resolveIndustries = (
	names: string[],
	industriesByName: Map<string, Industry>
): Industry[] =>
	names
		.map((name) => industriesByName.get(name.toLowerCase()))
		.filter((industry): industry is Industry => Boolean(industry));
